import type { CallFolder } from "./folders";
import type { CallListItem } from "./types";
import { formatDate, formatTime, formatWhen } from "./format";

/** How the Calls list is ordered. Date and recently added both use when the call started. */
export type CallSortKey = "date" | "added" | "accessed";

export type CallSortDirection = "asc" | "desc";

export const CALL_SORTS: { value: CallSortKey; label: string }[] = [
  { value: "accessed", label: "Last accessed" },
  { value: "added", label: "Recently added" },
  { value: "date", label: "Date" },
];

export const CALL_ORDERS: { value: CallSortDirection; label: string }[] = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
];

const ACCESS_KEY = "dooglie.calls.accessed";

/** The list arrives newest first. Date and recently added, descending, are that order. */
export function isServerOrder(
  sort: CallSortKey,
  direction: CallSortDirection,
  query: string,
): boolean {
  return query.trim() === "" && direction === "desc" && (sort === "added" || sort === "date");
}

function time(iso: string | null | undefined): number {
  const value = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(value) ? 0 : value;
}

/** Other people on the call, for the line next to the date. The signed-in user is on every row. */
export function listedPeople(call: CallListItem): string {
  return (call.attendees ?? [])
    .filter((person) => person.name.trim() && !person.is_user)
    .map((person) => person.name.trim())
    .join(", ");
}

function whenHaystack(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.toLocaleLowerCase();
  const clock = formatTime(iso);
  const shortHour = clock.replace(/^0/, "");
  const longMonth = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const numeric = date.toLocaleDateString("en-GB");
  return [formatWhen(iso), formatDate(iso), clock, shortHour, longMonth, numeric, iso.slice(0, 10)]
    .join(" ")
    .toLocaleLowerCase();
}

function haystack(call: CallListItem, folderNames: string): string {
  const names = (call.attendees ?? []).map((person) => person.name).join(" ");
  return `${call.title} ${names} ${folderNames} ${whenHaystack(call.created_at)}`.toLocaleLowerCase();
}

function folderHaystack(botId: string, folders: CallFolder[]): string {
  return folders
    .filter((folder) => folder.callIds.includes(botId))
    .map((folder) => folder.name)
    .join(" ");
}

export function arrangeCalls(
  calls: CallListItem[],
  options: {
    query: string;
    sort: CallSortKey;
    direction: CallSortDirection;
    accessed: Record<string, string>;
    /** Meetings inside a folder whose name matches are included. */
    folders?: CallFolder[];
  },
): CallListItem[] {
  const needle = options.query.trim().toLocaleLowerCase();
  const folders = options.folders ?? [];
  const matched = needle
    ? calls.filter((call) => haystack(call, folderHaystack(call.bot_id, folders)).includes(needle))
    : [...calls];
  const sign = options.direction === "asc" ? 1 : -1;
  matched.sort((a, b) => {
    let diff = 0;
    if (options.sort === "accessed") {
      diff = time(options.accessed[a.bot_id]) - time(options.accessed[b.bot_id]);
    } else {
      diff = time(a.created_at) - time(b.created_at);
    }
    if (diff !== 0) return diff * sign;
    const byDate = time(b.created_at) - time(a.created_at);
    if (byDate !== 0) return byDate;
    return a.bot_id.localeCompare(b.bot_id);
  });
  return matched;
}

/** Folders follow the same Sort by choice as the meetings. */
export function arrangeFolders(
  folders: CallFolder[],
  options: {
    sort: CallSortKey;
    direction: CallSortDirection;
    accessed: Record<string, string>;
    calls: Pick<CallListItem, "bot_id" | "created_at">[];
  },
): CallFolder[] {
  const sign = options.direction === "asc" ? 1 : -1;
  const callTime = new Map(options.calls.map((call) => [call.bot_id, time(call.created_at)]));
  const stamp = (folder: CallFolder) => {
    if (options.sort === "accessed") return time(options.accessed[folder.id]);
    if (options.sort === "added") return time(folder.createdAt);
    let latest = 0;
    for (const id of folder.callIds) latest = Math.max(latest, callTime.get(id) ?? 0);
    return latest || time(folder.createdAt);
  };
  return [...folders].sort((a, b) => {
    const diff = stamp(a) - stamp(b);
    if (diff !== 0) return diff * sign;
    return a.name.localeCompare(b.name);
  });
}

export function loadCallAccess(): Record<string, string> {
  try {
    const raw = localStorage.getItem(ACCESS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const access: Record<string, string> = {};
    for (const [id, at] of Object.entries(parsed)) {
      if (typeof at === "string" && !Number.isNaN(Date.parse(at))) access[id] = at;
    }
    return access;
  } catch {
    return {};
  }
}

/** This browser's last open of a call. The API does not record it. */
export function rememberCallAccess(botId: string, at = new Date().toISOString()): void {
  if (!botId) return;
  const current = loadCallAccess();
  current[botId] = at;
  const recent = Object.entries(current)
    .sort((a, b) => Date.parse(b[1]) - Date.parse(a[1]))
    .slice(0, 500);
  try {
    localStorage.setItem(ACCESS_KEY, JSON.stringify(Object.fromEntries(recent)));
  } catch {
    // A full or private store still leaves the list usable.
  }
}
